import React, { Component, type MouseEvent, type ReactNode } from 'react';
import classNames from 'classnames';
import { Button, Dialog, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';
import theme from '@wavetrak/theme';
import { NextRouter } from 'next/router';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';

import { CloseIcon, Search, SurflineLogo, SurflineWaterDrop } from 'components/Icons';
import GetAppModal from 'components/GetAppModal';
import TrackableLink from 'components/TrackableLink';
import WavetrakLink from 'components/WavetrakLink';
import type { MenuState, ServiceConfiguration, TaxonomyNavigatorSettings } from 'types/header';
import type { UserDetails, UserSettings } from 'types/user';
import createAccessibleOnClick from 'utils/createAccessibleOnClick';
import { isPremiumPlusUser, getIsPremiumWithAdsUser } from 'utils/user';
import { SL_WEB_GETAPP_QR_CODE_BUTTON } from 'common/treatments';
import { useTreatments } from 'utils/treatments';
import CamsReportsMenu from './components/CamsReportsMenu';
import LearnMenu from './components/LearnMenu';
import MapsMenu from './components/MapsMenu';
import NavigationItem from './components/NavigationItem';
import SiteSearch from './components/SiteSearch';
import WatchMenu from './components/WatchMenu';
import UserAvatar from './components/UserAvatar';
import UserMenu from './components/UserMenu';
import isPremiumOrVIP from '../helpers/isPremiumOrVip';
import isForecastOnlyEntitled from '../helpers/isForecastMember';
import getSignInHref from '../helpers/getSignInHref';

import styles from './NavigationBar.module.scss';

const SUBSCRIBE_EVENT_PROPERTIES = { location: 'button top' };
const DESKTOP_BREAKPOINT = 992;

export interface NavigationBarProps {
  accountAlert?: boolean | null;
  accountWarning?: boolean;
  badge?: ReactNode;
  conditionColorsClass?: string;
  entitlements?: Array<string>;
  freeTrialEligible?: boolean;
  hideCTA?: boolean;
  large?: boolean;
  serviceConfig: ServiceConfiguration;
  settings?: TaxonomyNavigatorSettings;
  user?: Partial<UserDetails> | null;
  userSettings: UserSettings | null;
  latestFeatureRelease?: {
    title: string;
    date: string;
    url?: string;
  };
  latestPremiumPerkRelease?: {
    date: string;
    url?: string;
  };
  showManageMembersLink?: boolean;
  treatments?: any;
}

interface NavigationBarState {
  camsReportsMenuState: MenuState;
  forecastsMenuState: MenuState;
  getAppModalOpen: boolean;
  mobileBreakpoint: number;
  mobileMenuTitle: any;
  renderMobileMenu: any;
  searchIsOpen: boolean;
  signInHref: string;
  viewport: any;
}

class NavigationBarClass extends Component<NavigationBarProps, NavigationBarState> {
  static defaultProps = {
    accountAlert: false,
    accountWarning: false,
    badge: null,
    entitlements: [],
    freeTrialEligible: false,
    hideCTA: false,
    large: false,
    settings: {},
    user: null,
    latestFeatureRelease: {},
  };

  static getSignInEventProperties = () => ({
    pageName: window.location.pathname,
    tabName: 'Sign In',
    linkName: 'Sign In',
    platform: 'web',
    isMobileView: window.innerWidth <= theme.breakpoints.values.tabletLg,
  });

  static getUpgradeCTA = (
    isForecastMember: boolean,
    isPremiumWithAdsMember: boolean,
    isFamilyPlanUser: boolean,
  ) => {
    if (isForecastMember || isPremiumWithAdsMember || isFamilyPlanUser) return 'Upgrade now';
    return 'Go Premium';
  };

  mobileMenuRef: any;

  resizeTimeout: any;

  signInLinkRef: any;

  constructor(props: NavigationBarProps) {
    super(props);

    this.state = {
      camsReportsMenuState: {
        error: false,
        loading: false,
        taxonomy: {
          activeNode: {},
          children: [],
          parents: [],
        },
      },
      forecastsMenuState: {
        error: false,
        loading: false,
        taxonomy: {
          activeNode: {},
          children: [],
          parents: [],
        },
      },
      getAppModalOpen: false,
      mobileBreakpoint: DESKTOP_BREAKPOINT,
      mobileMenuTitle: null,
      renderMobileMenu: null,
      signInHref: getSignInHref(null),
      searchIsOpen: false,
      viewport: null,
    };

    this.mobileMenuRef = null;
    this.resizeTimeout = null;
    this.signInLinkRef = React.createRef();
  }

  componentDidMount() {
    this.setViewport();
    const router = this.context as unknown as NextRouter;
    this.setState({ signInHref: getSignInHref(router.asPath) });
    window?.addEventListener('resize', this.resizeListener);
  }

  componentDidUpdate(prevProps: NavigationBarProps, prevState: NavigationBarState) {
    const router = this.context as unknown as NextRouter;
    const signInHref = getSignInHref(router.asPath);
    if (signInHref !== prevState.signInHref) {
      this.setState({
        signInHref,
      });
    }
  }

  componentWillUnmount() {
    window?.removeEventListener('resize', this.resizeListener);
    this.removeMobileMenu();
  }

  setViewport = () => {
    this.setState((prevState) => ({
      viewport: window?.innerWidth > prevState.mobileBreakpoint ? 'desktop' : 'mobile',
    }));
  };

  setMenuState = (menuType: string) => (stateUpdate: MenuState) => {
    this.setState(
      (prevState: Readonly<NavigationBarState>) =>
        ({
          [menuType]: {
            ...(prevState as any)[menuType],
            ...stateUpdate,
          },
        }) as any,
    );
  };

  setMobileMenu = (renderComponent: () => ReactNode, title: string) => {
    const { viewport } = this.state;
    if (viewport === 'mobile') {
      this.setState({
        renderMobileMenu: renderComponent,
        mobileMenuTitle: title,
      });
    }
  };

  removeMobileMenu = () => {
    this.setState({
      renderMobileMenu: null,
      searchIsOpen: false,
    });
  };

  resizeListener = () => {
    if (this.resizeTimeout) return;
    this.resizeTimeout = setTimeout(() => {
      this.resizeTimeout = null;
      this.updateScrollLock();
    }, 66);

    const { viewport, mobileBreakpoint } = this.state;
    if (viewport === 'mobile' && window.innerWidth > mobileBreakpoint) {
      this.setState({ viewport: 'desktop' });
    } else if (viewport === 'desktop' && window.innerWidth <= mobileBreakpoint) {
      this.setState({ viewport: 'mobile' });
    }
  };

  scrollMenuToTop = () => {
    if (this.mobileMenuRef) {
      this.mobileMenuRef.scrollTop = 0;
    }
  };

  updateScrollLock = () => {
    const { renderMobileMenu } = this.state;
    if (renderMobileMenu && window.innerWidth > DESKTOP_BREAKPOINT) {
      this.removeMobileMenu();
    }
  };

  handleClickedLink = (clickProperties: any, closeMobileMenu: boolean = false) => {
    if (clickProperties) {
      const { name, properties } = clickProperties;
      trackEvent(name, properties);
    }
    if (closeMobileMenu) {
      this.removeMobileMenu();
    }
  };

  handleCloseSearch = () => this.setState({ searchIsOpen: false });

  handleClickAccount = (event: MouseEvent) => {
    const { viewport } = this.state;
    if (viewport === 'mobile') event.preventDefault();
    this.setMobileMenu(this.renderUserMenu, 'Account');
  };

  renderCamsReportsMenu = () => {
    const { camsReportsMenuState, signInHref } = this.state;
    const { user, settings, serviceConfig, userSettings } = this.props;
    return (
      <CamsReportsMenu
        loggedIn={!!user}
        navigationSettings={settings?.navigation}
        recentlyVisited={settings?.recentlyVisited?.spots}
        serviceConfig={serviceConfig}
        menuState={camsReportsMenuState}
        setMenuState={this.setMenuState('camsReportsMenuState')}
        scrollMenuToTop={this.scrollMenuToTop}
        signInUrl={signInHref}
        onClickLink={this.handleClickedLink}
        userSettings={userSettings}
      />
    );
  };

  renderUserMenu = () => {
    const {
      user,
      entitlements,
      freeTrialEligible,
      accountWarning,
      userSettings,
      latestFeatureRelease,
      latestPremiumPerkRelease,
      showManageMembersLink,
    } = this.props;
    // date values are YYYY-MM strings, so a lexicographic comparison matches chronological order
    // and keeps the dot cleared if another platform (e.g. iOS) saved a later date for the same flag.
    const releaseDate = latestFeatureRelease?.date;
    const showFeatureReleaseNotification = releaseDate
      ? (userSettings?.releaseAnnouncementDate ?? '') < releaseDate
      : false;
    const perkDate = latestPremiumPerkRelease?.date;
    const showPremiumPerksNotification = perkDate
      ? (userSettings?.premiumPerkDate ?? '') < perkDate
      : false;
    return (
      <UserMenu
        user={user}
        entitlements={entitlements}
        freeTrialEligible={freeTrialEligible}
        accountWarning={accountWarning}
        showDetails
        onClickLink={this.handleClickedLink}
        featureRelease={latestFeatureRelease}
        premiumPerks={latestPremiumPerkRelease}
        showFeatureReleaseNotification={showFeatureReleaseNotification}
        showPremiumPerksNotification={showPremiumPerksNotification}
        showManageMembersLink={showManageMembersLink}
      />
    );
  };

  renderMapsMenu = () => <MapsMenu onClickLink={this.handleClickedLink} />;

  renderLearnMenu = () => <LearnMenu onClickLink={this.handleClickedLink} />;

  renderWatchMenu = () => <WatchMenu onClickLink={this.handleClickedLink} />;

  handleClickLearn = (event: MouseEvent) => {
    event.preventDefault();
    this.setMobileMenu(this.renderLearnMenu, 'Learn');
  };

  handleClickWatch = (event: MouseEvent) => {
    event.preventDefault();
    this.setMobileMenu(this.renderWatchMenu, 'Watch');
  };

  handleClickAbout = () =>
    this.handleClickedLink({
      name: 'Clicked Main Nav',
      properties: {
        completion: true,
        destinationUrl: 'https://www.surfline.com/lp/our-commitments',
        linkName: 'About',
        tabName: 'About',
        clickEndLocation: 'About',
      },
    });

  handleClickTrial = () =>
    this.handleClickedLink(
      { name: 'Clicked Subscribe CTA', properties: SUBSCRIBE_EVENT_PROPERTIES },
      true,
    );

  handleClickGetApp = () => {
    trackEvent('Clicked CTA', {
      pageName: window.location.pathname,
      ctaText: 'Get App',
      location: 'header',
    });
    this.setState({ getAppModalOpen: true });
  };

  handleClickReports = (event: MouseEvent) => {
    event.preventDefault();
    this.setMobileMenu(this.renderCamsReportsMenu, 'Cams');
  };

  handleClickMaps = (event: MouseEvent) => {
    event.preventDefault();
    this.setMobileMenu(this.renderMapsMenu, 'Maps');
  };

  render() {
    const {
      getAppModalOpen,
      renderMobileMenu,
      mobileMenuTitle,
      searchIsOpen,
      signInHref,
      viewport,
    } = this.state;
    const {
      accountAlert,
      accountWarning,
      conditionColorsClass,
      entitlements,
      hideCTA,
      latestFeatureRelease,
      latestPremiumPerkRelease,
      serviceConfig,
      user,
      userSettings,
      treatments,
    } = this.props;

    const isForecastMember = isForecastOnlyEntitled(entitlements);
    const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);
    const isFamilyPlanUser = entitlements?.includes('sl_family') ?? false;

    const isGetAppButtonEnabled = treatments?.[SL_WEB_GETAPP_QR_CODE_BUTTON] === 'on';

    // date values are YYYY-MM strings, so a lexicographic comparison matches chronological order
    // and keeps the dot cleared if another platform (e.g. iOS) saved a later date for the same flag.
    const releaseDate = latestFeatureRelease?.date;
    const showFeatureReleaseNotification = releaseDate
      ? (userSettings?.releaseAnnouncementDate ?? '') < releaseDate
      : false;
    const perkDate = latestPremiumPerkRelease?.date;
    const showPremiumPerksNotification = perkDate
      ? (userSettings?.premiumPerkDate ?? '') < perkDate
      : false;
    const isPremiumPlusUserStatus = isPremiumPlusUser(entitlements);
    const accountHref = '/account';
    const userAvatar = user ? (
      <UserAvatar
        accountAlert={accountAlert}
        accountWarning={accountWarning}
        isPremiumPlusUser={isPremiumPlusUserStatus}
        showNotification={showFeatureReleaseNotification || showPremiumPerksNotification}
        profilePictureUrl={user.profilePictureUrl}
      />
    ) : null;

    return (
      <nav
        className={classNames('quiver-new-navigation-bar', styles.navigationBar, {
          [styles.userNotLoggedIn]: !user,
        })}
        aria-label="Primary navigation"
      >
        <div className={styles.content}>
          <div className={styles.logoDesktop} data-testid="navigation-bar-logo-desktop">
            <WavetrakLink href="/" isExternal>
              <SurflineLogo />
            </WavetrakLink>
          </div>
          <div className={styles.logoMobile} data-testid="navigation-bar-logo-mobile">
            <WavetrakLink href="/" isExternal>
              <SurflineWaterDrop />
            </WavetrakLink>
          </div>
          <div className={styles.navigation}>
            <NavigationItem linkDisplay="Cams & Forecasts" href="/surf-reports-forecasts-cams">
              <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')}>
                {this.renderCamsReportsMenu()}
              </div>
            </NavigationItem>
            <NavigationItem linkDisplay="Maps" href="/surf-reports-forecasts-cams-map">
              <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')}>
                <MapsMenu onClickLink={this.handleClickedLink} />
              </div>
            </NavigationItem>
            <NavigationItem linkDisplay="Learn" href="/travel">
              <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')}>
                {this.renderLearnMenu()}
              </div>
            </NavigationItem>
            <NavigationItem linkDisplay="Watch" href="/series/swell-stories">
              <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')}>
                {this.renderWatchMenu()}
              </div>
            </NavigationItem>
            <NavigationItem
              linkDisplay="About"
              href="https://www.surfline.com/lp/our-commitments"
              isExternal
              newWindow
              onClick={this.handleClickAbout}
            >
              <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')} />
            </NavigationItem>
          </div>
          <div className={styles.siteSearch}>
            <div
              {...createAccessibleOnClick(() => this.setState({ searchIsOpen: true }), 'button')}
              className={styles.searchButton}
            >
              <Search className={styles.searchButtonIcon} />
              <Typography variant="footnote" component="span">
                Search
              </Typography>
            </div>
            <SiteSearch
              closeSearch={this.handleCloseSearch}
              serviceUrl={serviceConfig?.serviceUrl ?? ''}
              searchIsOpen={searchIsOpen}
              viewport={viewport}
            />
          </div>
          <div className={styles.cta}>
            {isGetAppButtonEnabled && (
              <Button
                className={styles.getAppButton}
                data-testid="navbar-get-app"
                onClick={this.handleClickGetApp}
                size="small"
                variant="secondary"
              >
                Get App
              </Button>
            )}
            {!hideCTA && (!user || !isPremiumOrVIP(entitlements)) ? (
              <Button
                className={styles.ctaLink}
                data-testid="navbar-trial-cta"
                href="/upgrade"
                onClick={this.handleClickTrial}
                size="small"
                variant="primary"
              >
                {NavigationBarClass.getUpgradeCTA(
                  isForecastMember,
                  isPremiumWithAdsUser,
                  isFamilyPlanUser,
                )}
              </Button>
            ) : null}
          </div>
          {user ? (
            <div
              className={classNames(styles.account, {
                [styles.userMenuWithGetAppButton]: isGetAppButtonEnabled,
              })}
            >
              <NavigationItem
                testId="navigation-item-user"
                href={accountHref}
                isAvatar
                isExternal
                linkDisplay={userAvatar}
                onClick={this.handleClickAccount}
                role="button"
                tabIndex={0}
                ariaLabel="Account"
              >
                <div className={classNames(styles.menuWrapper, 'sl-menu-wrapper')}>
                  {this.renderUserMenu()}
                </div>
              </NavigationItem>
            </div>
          ) : (
            <TrackableLink
              eventName="Clicked Main Nav"
              eventProperties={NavigationBarClass.getSignInEventProperties}
              ref={this.signInLinkRef}
            >
              <Typography
                data-testid="navbar-signin"
                variant="subHeadline"
                component="a"
                ref={this.signInLinkRef}
                href={signInHref}
                className={styles.signin}
              >
                Sign In
              </Typography>
            </TrackableLink>
          )}
        </div>
        <div className={styles.mobile}>
          <div className={styles.mobileContent}>
            <div className={styles.mobileContainer}>
              <div className={styles.mobileNav} data-testid="mobile-nav">
                <Dialog open={renderMobileMenu !== null}>
                  {renderMobileMenu !== null && (
                    <div className={classNames(styles.mobileNavMenu, conditionColorsClass)}>
                      <div className={styles.mobileNavMenuInner}>
                        <a
                          className={styles.mobileNavMenuClose}
                          data-testid="mobile-nav-close"
                          {...createAccessibleOnClick(
                            () =>
                              this.handleClickedLink(
                                {
                                  name: 'Clicked Main Nav',
                                  properties: {
                                    completion: false,
                                    linkName: 'close',
                                    tabName: mobileMenuTitle,
                                  },
                                },
                                true,
                              ),
                            'button',
                          )}
                        >
                          <span />
                          <Typography
                            className={styles.mobileNavMenuCloseTitle}
                            component="span"
                            data-testid="mobile-nav-title"
                            variant="subHeadline"
                          >
                            {mobileMenuTitle}
                          </Typography>
                          <CloseIcon />
                        </a>
                        {renderMobileMenu()}
                      </div>
                    </div>
                  )}
                </Dialog>
                <NavigationItem
                  href="/surf-reports-forecasts-cams"
                  linkDisplay="Cams & Forecasts"
                  onClick={this.handleClickReports}
                  role="button"
                  tabIndex={0}
                  testId="mobile-navigation-item"
                />
                <NavigationItem
                  href="/surf-reports-forecasts-cams-map"
                  linkDisplay="Maps"
                  onClick={this.handleClickMaps}
                  role="button"
                  tabIndex={0}
                  testId="mobile-navigation-item"
                />
                <NavigationItem
                  href="/travel"
                  linkDisplay="Learn"
                  onClick={this.handleClickLearn}
                  role="button"
                  tabIndex={0}
                  testId="mobile-navigation-item"
                />
                <NavigationItem
                  href="/series/swell-stories"
                  linkDisplay="Watch"
                  onClick={this.handleClickWatch}
                  role="button"
                  tabIndex={0}
                  testId="mobile-navigation-item"
                />
                <NavigationItem
                  href="https://www.surfline.com/lp/our-commitments"
                  isExternal
                  newWindow
                  linkDisplay="About"
                  onClick={this.handleClickAbout}
                  role="button"
                  tabIndex={0}
                  testId="mobile-navigation-item"
                />
              </div>
            </div>
          </div>
        </div>
        {isGetAppButtonEnabled && (
          <GetAppModal
            open={getAppModalOpen}
            setOpen={(open) => this.setState({ getAppModalOpen: open })}
          />
        )}
      </nav>
    );
  }
}

NavigationBarClass.contextType = RouterContext;

// Wrapper function component that uses the useTreatments hook
const NavigationBar = (props: NavigationBarProps) => {
  const treatments = useTreatments();
  return <NavigationBarClass {...props} treatments={treatments} />;
};

// Copy static methods to the wrapper component
NavigationBar.getSignInEventProperties = NavigationBarClass.getSignInEventProperties;
NavigationBar.getUpgradeCTA = NavigationBarClass.getUpgradeCTA;

export default NavigationBar;
